import { action, computed, makeObservable, observable } from 'mobx';
import { CollectionModelGammaBrightnessCorrection } from '../model/collection/brightnessCorrection/gamma.brightnessCorrection';
import { CollectionModelFreeCropParameter } from '../model/collection/cropParameter/free.cropParameter';
import { CollectionModelHiddenImageVisibility } from '../model/collection/imageVisibility/hidden.imageVisibility';
import { IkeUnit } from '../unit';
import { ArrangeAnnotations } from './ArrangeAnnotations';
import { MAX_ZOOM_LEVEL, START_ZOOM_LEVEL } from './bindings/image.zoom.binding';
import type { IkeDraw } from './draw';
import type { SVGRenderOptions } from './draw/svg/svg.render';

export enum IkeDrawTool {
	MARKER = 'marker',
	LINE = 'line',
	LINK = 'link',
	BASE = 'base',
}

export const DEFAULT_SCALE = 4;
export class IkeDrawState {
	draw: IkeDraw;
	static readonly BRIGHTNESS_DELTA = 1.2;
	static readonly BRIGHTNESS_MIN = 0.2;
	static readonly BRIGHTNESS_MAX = 5;
	static readonly BRIGHTNESS_APPROXIMATION_WINDOW = 0.1;
	static readonly BRIGHTNESS_DEFAULT = 1;
	static readonly IMAGE_VISIBILITY_HIDDEN_DEFAULT = false;

	static readonly ZOOM_DELTA = 1.1;
	static readonly ZOOM_MAX = MAX_ZOOM_LEVEL;
	static readonly ZOOM_DEFAULT = START_ZOOM_LEVEL;

	static readonly CABLEMEASURE_DEFAULT = 1; // default cablemesure value in inch
	static readonly CABLEMEASURE_STEP = 0.5;
	static readonly CABLEMEASURE_MIN = 0.5;
	static readonly CABLEMEASURE_MAX = 4;

	static readonly CROP_DEFAULT = 0; //default setting for x,y,width, and height.

	@observable zoom: number = IkeDrawState.ZOOM_DEFAULT;

	@observable cablemeasure: number = IkeDrawState.CABLEMEASURE_DEFAULT;
	@observable showCablemeasure: boolean = false;

	@observable private _brightness: number | null = 1;
	@observable private _imageVisibilityHidden: boolean = false;

	@observable private _cropX: number | null = 0;
	@observable private _cropY: number | null = 0;
	@observable private _cropWidth: number | null = 0;
	@observable private _cropHeight: number | null = 0;

	/**
	 * this scale is actually static, the zoom is used to determine how large things are drawn on the screen.
	 * often times the svgRender ctx/options will have a 'scale' property, which is calculated using zoom level and NOT this scale.
	 */
	@observable scale: number = DEFAULT_SCALE;

	@observable centerX: number = 0;
	@observable centerY: number = 0;

	@observable selectedRegion: string | null = null;
	@observable selectedId: string | null = null;
	@observable brightnessCorrectionId: string | null = null;
	@observable imageVisibilityId: string | null = null;
	@observable defaultBrightness: number = 1;
	@observable defaultImageVisibilityHidden: boolean = false;
	@observable defaultCrop = { x: 0, y: 0, width: 0, height: 0 };
	@observable cropParameterId: string | null = null;
	@observable hoverId: string | null = null;
	@observable moveId: string | null = null;
	@observable linkFieldIds: string[] = [];

	/* Trigger the properties dialog */
	@observable propertiesId: string | null = null;

	@observable isCrosshairVisible: boolean = true;
	@observable isDateLabelVisible: boolean = false;
	@observable isBearingLabelVisible: boolean = false;
	@observable isRollVisible: boolean = false;

	@observable isInfoVisible: boolean = false;

	@observable visibleInsightMasks: boolean = false;
	@observable visibleInsightMarkers: boolean = false;

	@observable autoArrangeAnnotations: boolean = false;

	@observable mouseDownX: number | null = null;
	@observable mouseDownY: number | null = null;

	@observable _tool: IkeDrawTool | null = null;

	constructor(draw: IkeDraw) {
		makeObservable(this);

		this.draw = draw;

		//if there is an already existing brightnessCorrection value, we apply it.
		const values = this.draw.brightnessCorrectionState.values();
		let id = '';
		//We go through all the brightness correction for this capture and keep the last id (there shall be only one brightness correction)
		for (let val of values) {
			id = val.id;
		}
		if (draw.brightnessCorrectionState.hasBrightnessCorrection(id)) {
			const gamma =
				draw.brightnessCorrectionState.getBrightnessCorrection<CollectionModelGammaBrightnessCorrection>(id);
			this.setBrightnessCorrectionId(id);
			this.setBrightness(gamma.exponent);
			this.defaultBrightness = gamma.exponent;
		}

		if (draw.imageVisibilityState.hasImageVisibility()) {
			const visibility = draw.imageVisibilityState.getImageVisibility<CollectionModelHiddenImageVisibility>();
			this.setImageVisibilityId(visibility.id);
			this.setImageVisibilityHidden(visibility.hidden);
			this.defaultImageVisibilityHidden = visibility.hidden;
		}

		const cropValues = this.draw.cropParameterState.values();
		let cropId = '';
		for (let val of cropValues) {
			cropId = val.id;
		}
		if (draw.cropParameterState.hasCropParameter(cropId)) {
			const free = draw.cropParameterState.getCropParameter<CollectionModelFreeCropParameter>(cropId);
			this.setCropParameterId(cropId);
			this.setCrop(free.x, free.y, free.width, free.height);
			if (free.width > 0 && free.height > 0) {
				this.setZoom(draw.capture.resolution.width / free.width); //zoom lvl
				this.setCenter(free.x + free.width * 0.5, free.y + free.height * 0.5);
			}
			this.defaultCrop = free;
		}

		if (this.draw.drawState.hasInsightDetections()) {
			this.visibleInsightMarkers = true;
		}
	}

	@computed get tool() {
		// If the image is not classed as a markable image, then no tools are available.
		if (!this.draw.isMarkable) {
			return null;
		}

		// If the image contains any user created measurements, then use the selected tool
		// Note: A standard base measurement is considered a user measurement
		if (this.draw.drawState.hasUserMeasurements()) {
			return this._tool;
		}

		// Otherwise, only the base tool is available
		return 'base';
	}

	@action setTool(tool: IkeDrawTool | null) {
		this._tool = tool;
		this.linkFieldIds = [];
	}

	@action toggleTool(tool: IkeDrawTool) {
		if (this.tool === tool) {
			this._tool = null;
		} else {
			this._tool = tool;
		}
	}

	@computed get brightness() {
		const brightness = this._brightness;
		if (brightness == null || isNaN(brightness)) {
			return IkeDrawState.BRIGHTNESS_DEFAULT;
		}
		return brightness;
	}

	@computed get imageVisibilityHidden(): boolean {
		const imageHidden = this._imageVisibilityHidden;
		if (!imageHidden) {
			return IkeDrawState.IMAGE_VISIBILITY_HIDDEN_DEFAULT;
		}
		return imageHidden;
	}

	@action setBrightness(brightness: number) {
		if (brightness == null) {
			this._brightness = null;
			return;
		}

		//if there is no existing brightness correction state yet, we create one before setting its value.
		if (this.brightnessCorrectionId == null) {
			const newGammaId = this.draw.brightnessCorrectionState.Mutator.create('gamma');
			this.setBrightnessCorrectionId(newGammaId);
		}

		if (
			brightness > IkeDrawState.BRIGHTNESS_DEFAULT - IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW &&
			brightness < IkeDrawState.BRIGHTNESS_DEFAULT + IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW
		) {
			this._brightness = IkeUnit.round(brightness, 2);
		} else {
			this._brightness = IkeUnit.round(brightness, 5);
		}

		if (this.brightnessCorrectionId) {
			const gamma = this.draw.brightnessCorrectionState.getBrightnessCorrection(this.brightnessCorrectionId);
			if (CollectionModelGammaBrightnessCorrection.isGamma(gamma)) {
				gamma.Mutator.setExponent(this._brightness);
			}
		}
	}

	@action setImageVisibilityHidden(hidden: boolean) {
		//if there is no existing image visibility state yet, we create one before setting its value.
		if (this.imageVisibilityId == null) {
			const newHiddenImageVisibilityId = this.draw.imageVisibilityState.Mutator.create('hidden');
			this.setImageVisibilityId(newHiddenImageVisibilityId);
		}

		const visibility = this.draw.imageVisibilityState.getImageVisibility();
		this._imageVisibilityHidden = hidden;
		if (CollectionModelHiddenImageVisibility.isHidden(visibility)) {
			visibility.Mutator.setHidden(hidden);
		}
	}

	@action toggleImageVisibility() {
		this.setImageVisibilityHidden(!this._imageVisibilityHidden);
	}

	@action brightnessIncrease() {
		if (this.canIncreaseBrightness) {
			const newBrightness = this.brightness / IkeDrawState.BRIGHTNESS_DELTA;
			this.setBrightness(newBrightness);
		}
	}

	@action brightnessDecrease() {
		if (this.canDecreaseBrightness) {
			const newBrightness = this.brightness * IkeDrawState.BRIGHTNESS_DELTA;
			this.setBrightness(newBrightness);
		}
	}

	@computed get canIncreaseBrightness() {
		const result = this.brightness > IkeDrawState.BRIGHTNESS_MIN;
		return result;
	}

	@computed get canDecreaseBrightness() {
		const result = this.brightness < IkeDrawState.BRIGHTNESS_MAX;
		return result;
	}

	@computed get canIncreaseCablemeasure() {
		return this.cablemeasure + IkeDrawState.CABLEMEASURE_STEP <= IkeDrawState.CABLEMEASURE_MAX;
	}

	@computed get canDecreaseCablemeasure() {
		return this.cablemeasure - IkeDrawState.CABLEMEASURE_STEP >= IkeDrawState.CABLEMEASURE_MIN;
	}

	@action brightnessReset() {
		this.setBrightness(IkeDrawState.BRIGHTNESS_DEFAULT);
	}

	@computed get markerOffsetWidth() {
		return 200 - this.zoom * 10;
	}

	@computed get cropX() {
		const cropX = this._cropX;
		if (cropX == null || isNaN(cropX)) {
			return IkeDrawState.CROP_DEFAULT;
		}
		return cropX;
	}

	@computed get cropY() {
		const cropY = this._cropY;
		if (cropY == null || isNaN(cropY)) {
			return IkeDrawState.CROP_DEFAULT;
		}
		return cropY;
	}

	@computed get cropWidth() {
		const cropWidth = this._cropWidth;
		if (cropWidth == null || isNaN(cropWidth)) {
			return IkeDrawState.CROP_DEFAULT;
		}
		return cropWidth;
	}

	@computed get cropHeight() {
		const cropHeight = this._cropHeight;
		if (cropHeight == null || isNaN(cropHeight)) {
			return IkeDrawState.CROP_DEFAULT;
		}
		return cropHeight;
	}

	@action setCrop(x: number, y: number, width: number, height: number) {
		if (x == null || y == null || width == null || height == null) {
			this._cropX = null;
			this._cropY = null;
			this._cropWidth = null;
			this._cropHeight = null;
			return;
		}

		//if there is no existing crop parameter state yet, we create one before setting its value.
		if (this.cropParameterId == null) {
			const newFreeId = this.draw.cropParameterState.Mutator.create('free');
			this.setCropParameterId(newFreeId);
		}

		this._cropX = x;
		this._cropY = y;
		this._cropWidth = width;
		this._cropHeight = height;

		if (this.cropParameterId) {
			const free = this.draw.cropParameterState.getCropParameter(this.cropParameterId);
			if (CollectionModelFreeCropParameter.isFree(free)) {
				free.Mutator.setCrop(this._cropX, this._cropY, this._cropWidth, this._cropHeight);
			}
		}
	}

	@action cropReset() {
		this.setCrop(
			IkeDrawState.CROP_DEFAULT,
			IkeDrawState.CROP_DEFAULT,
			IkeDrawState.CROP_DEFAULT,
			IkeDrawState.CROP_DEFAULT
		);
	}

	///////////////////////////////////
	@action setCrosshair(show: boolean) {
		this.isCrosshairVisible = show;
	}

	@action setDatelabel(show: boolean) {
		this.isDateLabelVisible = show;
	}

	@action setBearingLabelVisible(show: boolean) {
		this.isBearingLabelVisible = show;
	}

	@computed get center() {
		return {
			x: this.centerX,
			y: this.centerY,
		};
	}

	@action setHovered(hoverId: string) {
		this.hoverId = hoverId;
	}

	@action setCenter(x: number, y: number) {
		this.centerX = x;
		this.centerY = y;
	}

	@action setSelected(id: string | null, region: string | null) {
		this.selectedId = id;
		this.selectedRegion = region;
	}

	@action setBrightnessCorrectionId(id: string) {
		this.brightnessCorrectionId = id;
	}

	@action setImageVisibilityId(id: string) {
		this.imageVisibilityId = id;
	}

	@action setCropParameterId(id: string) {
		this.cropParameterId = id;
	}

	@action setZoom(zoom: number, x?: number, y?: number) {
		if (x != null && y != null) {
			this.setCenter(x, y);
		}
		this.zoom = IkeUnit.round(zoom, 2);
	}

	@action zoomIncrease() {
		this.setZoom(Math.min(IkeDrawState.ZOOM_MAX, this.zoom * IkeDrawState.ZOOM_DELTA));
	}

	@action zoomDecrease() {
		this.setZoom(Math.max(IkeDrawState.ZOOM_DEFAULT, this.zoom * (1 / IkeDrawState.ZOOM_DELTA)));
	}

	@action zoomReset() {
		this.setZoom(IkeDrawState.ZOOM_DEFAULT);
	}

	@action toggleCablemeasure() {
		this.showCablemeasure = !this.showCablemeasure;
	}

	@action cablemeasureIncrease() {
		let actualMeasure = this.cablemeasure + IkeDrawState.CABLEMEASURE_STEP;
		if (actualMeasure > IkeDrawState.CABLEMEASURE_MAX) {
			actualMeasure = IkeDrawState.CABLEMEASURE_MAX;
		}

		this.cablemeasure = actualMeasure;
	}

	@action cablemeasureDecrease() {
		let actualMeasure = this.cablemeasure - IkeDrawState.CABLEMEASURE_STEP;
		if (actualMeasure < IkeDrawState.CABLEMEASURE_MIN) {
			actualMeasure = IkeDrawState.CABLEMEASURE_MIN;
		}

		this.cablemeasure = actualMeasure;
	}

	@action propertiesShow(drawableId: string) {
		this.propertiesId = drawableId;
	}

	@action propertiesHide() {
		this.propertiesId = null;
	}

	@action setLinkFields(fieldIds: string[] | null) {
		if (fieldIds == null || fieldIds.length === 0) {
			this.linkFieldIds = [];
			if (this.tool !== IkeDrawTool.BASE) {
				this._tool = null;
			}
		} else {
			this.linkFieldIds = fieldIds;
			this._tool = IkeDrawTool.MARKER;
		}
	}

	setFromState(other: IkeDrawState) {
		if (other == null) {
			return;
		}

		if (this.brightness !== other.brightness) {
			this.setBrightness(other.brightness);
		}
		if (
			this.cropX !== other.cropX ||
			this.cropY !== other.cropY ||
			this.cropHeight !== other.cropHeight ||
			this.cropWidth !== other.cropWidth
		) {
			this.setCrop(other.cropX, other.cropY, other.cropWidth, other.cropHeight);
		}
		this.setZoom(other.zoom, other.centerX, other.centerY);
	}

	setInfoVisible(visible: boolean) {
		this.isInfoVisible = visible;
		this.isBearingLabelVisible = visible;
		this.isDateLabelVisible = visible;
		this.isRollVisible = visible;
	}

	@action setInsightMasksVisible(visible: boolean) {
		this.visibleInsightMasks = visible;
	}

	@action setInsightMarkersVisible(visible: boolean) {
		this.visibleInsightMarkers = visible;
	}

	setAutoArrangeAnnotations(autoArrange: boolean, renderOptions?: SVGRenderOptions) {
		this.autoArrangeAnnotations = autoArrange;
		if (this.autoArrangeAnnotations && renderOptions) {
			this.draw.state.arrangeAnnotations(renderOptions);
		}
	}

	@action arrangeAnnotations(renderOptions: SVGRenderOptions) {
		new ArrangeAnnotations(this.draw, renderOptions).run();
	}
}
